/* Hub Stand Height Setting: port of IntelAlign's HubStandHeightCalculatorModel.swift,
   with the Tire Deflection estimate (TireGeometry.swift + SLRCalculatorModel.swift) for
   Step 2. Five steps per corner:
     1  ride height ON TIRES (any repeatable spot)
     2  approximate Diameter Setting per axle (loaded tire diameter, 2 x SLR, or typed)
     3  set the stands, lower the car onto the pads
     4  ride height ON HUB STANDS, same spots
     5  refined = axle setting + (on tires - on Hub Stands) x 2
   Doubled because ride height moves with the RADIUS while the setting is a diameter.
   Left and right are averaged per axle; with a Hub Mount Plate tier selected, both axles
   shift by one EVEN amount to fit the plate's range, and half of it is the ride-height
   compensation. The math is Node-requireable for the tests; the page wiring follows. */
(function (root) {
  'use strict';

  var MM_PER_IN = 25.4, KGF_PER_LB = 0.45359237, KPA_PER_PSI = 6.894757, N_PER_KGF = 9.80665;
  var CORNERS = ['LF', 'RF', 'LR', 'RR'];
  var NAMES = { LF: 'Left front', RF: 'Right front', LR: 'Left rear', RR: 'Right rear' };
  var isFront = function (c) { return c === 'LF' || c === 'RF'; };

  /* CSM Hub Mount Plate tiers: the Diameter Setting range each can reach, tallest first. */
  var TIERS = [
    { id: 0, min: 662, max: 736, inch: '26–29 in' },
    { id: 1, min: 636, max: 710, inch: '25–28 in' },
    { id: 2, min: 610, max: 684, inch: '24–27 in' },
    { id: 3, min: 586, max: 660, inch: '23–26 in' },
    { id: 4, min: 560, max: 634, inch: '22–25 in' },
    { id: 5, min: 534, max: 608, inch: '21–24 in' },
    { id: 6, min: 508, max: 584, inch: '20–23 in' },
    { id: 7, min: 484, max: 558, inch: '19–22 in' }
  ];
  var tierLabel = function (t) { return t.min + '–' + t.max + ' mm (' + t.inch + ')'; };

  /* ---------- Step 5 ---------- */
  function refined(st, c) {
    var base = isFront(c) ? st.frontSetting : st.rearSetting;
    var tires = st.onTires[c], stands = st.onStands[c];
    if (base == null || tires == null || stands == null) return null;
    return base + (tires - stands) * 2;
  }
  function hasAll(st) { return CORNERS.every(function (c) { return refined(st, c) !== null; }); }
  function axleBase(st, c) {
    var axle = isFront(c) ? ['LF', 'RF'] : ['LR', 'RR'];
    var present = axle.map(function (a) { return refined(st, a); }).filter(function (v) { return v !== null; });
    if (present.length === axle.length) return (present[0] + present[1]) / 2;
    return refined(st, c);
  }
  function fitOffset(st) {
    var t = st.tier; if (!t || !hasAll(st)) return null;
    var front = axleBase(st, 'LF'), rear = axleBase(st, 'LR');
    var lo = Math.min(front, rear), hi = Math.max(front, rear);
    var cMin = Math.ceil((t.min - lo) / 2), cMax = Math.floor((t.max - hi) / 2);
    if (cMin > cMax) return null;
    return 2 * Math.min(Math.max(0, cMin), cMax);
  }
  function plateRangeExceeded(st) { return !!st.tier && hasAll(st) && fitOffset(st) === null; }
  function fitted(st, c) { var b = axleBase(st, c); if (b === null) return null; var k = fitOffset(st); return b + (k || 0); }
  function rideHeightComp(st) { var k = fitOffset(st); return k ? k / 2 : null; }

  /* ---------- Tire deflection (Rhyne tangent vertical stiffness, 2005) ---------- */
  function effAR(odMM, sn, rimIN) { return 100 * (odMM - rimIN * MM_PER_IN) / (2 * sn); }
  function parseTire(str) {
    var s = String(str == null ? '' : str).trim(), m;
    if (!s) return null;
    if ((m = /^\s*(\d{2}(?:\.\d)?)\s*[xX]\s*(\d{1,2}(?:\.\d)?)\s*-\s*(\d{2}(?:\.\d)?)\s*$/i.exec(s))) {
      var od = +m[1] * MM_PER_IN, sn = +m[2] * MM_PER_IN;
      return { sectionWidthMM: sn, aspectRatio: effAR(od, sn, +m[3]), rimIN: +m[3], isSlick: true };
    }
    if ((m = /^\s*(\d{3})\s*\/\s*(\d{2})\s*(?:Z?R|D|B|-)\s*(\d{2}(?:\.\d)?)\s*$/i.exec(s)))
      return { sectionWidthMM: +m[1], aspectRatio: +m[2], rimIN: +m[3], isSlick: false };
    if ((m = /^\s*(\d{3})\s*\/\s*(\d{3})\s*(?:R|-)\s*(\d{2}(?:\.\d)?)\s*$/i.exec(s)))
      return { sectionWidthMM: +m[1], aspectRatio: effAR(+m[2], +m[1], +m[3]), rimIN: +m[3], isSlick: true };
    if ((m = /^\s*(\d{2})\s*\/\s*(\d{2})\s*(?:R|-)\s*(\d{2}(?:\.\d)?)\s*$/i.exec(s))) {
      var sn2 = +m[1] * 10, od2 = +m[2] * 10;
      return { sectionWidthMM: sn2, aspectRatio: effAR(od2, sn2, +m[3]), rimIN: +m[3], isSlick: true };
    }
    return null;
  }
  var overallDiameterMM = function (t) { return t.rimIN * MM_PER_IN + 2 * t.sectionWidthMM * (t.aspectRatio / 100); };
  var sectionHeightMM = function (t) { return t.sectionWidthMM * t.aspectRatio / 100; };
  function stiffnessKgfPerMM(pKPa, sn, ar, od) { return 0.00028 * pKPa * Math.sqrt((1.03 - 0.004 * ar) * sn * od) + 3.45; }
  function solve(size, psi, lb, odOverrideMM, cal) {
    var hasOverride = odOverrideMM != null, c = cal == null ? 1 : cal;
    var od = hasOverride ? odOverrideMM : overallDiameterMM(size);
    var kz = stiffnessKgfPerMM(psi * KPA_PER_PSI, size.sectionWidthMM, size.aspectRatio, od) * c;
    var d = (lb * KGF_PER_LB) / kz, r = od / 2;
    var sh = hasOverride ? (od - size.rimIN * MM_PER_IN) / 2 : sectionHeightMM(size);
    return { overallDiameterMM: od, freeRadiusMM: r, stiffnessKgfPerMM: kz, stiffnessNPerMM: kz * N_PER_KGF, deflectionMM: d,
             staticLoadedRadiusMM: r - d, staticLoadedDiameterMM: 2 * (r - d), effectiveRollingRadiusMM: r - d / 3,
             deflectionPctOfSection: 100 * d / sh, deflectionExceedsModelRange: 100 * d / sh > 25,
             usedODOverride: hasOverride, isSlick: size.isSlick };
  }

  /* The size pickers, exactly the app's lists (the parser is digit-count specific). */
  var range = function (a, b, s) { var o = []; for (var v = a; v <= b + 1e-9; v += s) o.push(Math.round(v * 10) / 10); return o; };
  var OPTIONS = {
    streetWidths: range(155, 405, 5), streetAspects: range(25, 85, 5), rims: range(13, 24, 1),
    slickODsIN: range(18, 33, 1), slickWidthsIN: range(6, 16, 1),
    slickWidthsMM: range(180, 395, 5), slickODsMM: range(480, 760, 5),
    slickWidthsCM: range(18, 39, 1), slickODsCM: range(48, 79, 1)
  };
  var rimText = function (v) { return v === Math.round(v) ? String(Math.round(v)) : v.toFixed(1); };
  function sizeText(t) {
    var rim = rimText(t.rim);
    switch (t.format) {
      case 'slickIN': return t.odIN.toFixed(1) + 'x' + t.widthIN.toFixed(1) + '-' + rim;
      case 'slickMM': return Math.round(t.widthMM) + '/' + Math.round(t.odMM) + '-' + rim;
      case 'slickCM': return Math.round(t.widthCM) + '/' + Math.round(t.odCM) + '-' + rim;
      default:        return Math.round(t.width) + '/' + Math.round(t.aspect) + 'R' + rim;
    }
  }
  function tireResult(t) {
    var size = parseTire(sizeText(t));
    if (!size || !(sectionHeightMM(size) > 0) || !(t.psi > 0) || !(t.load > 0) || !(t.cal > 0)) return null;
    return solve(size, t.psi, t.load, null, t.cal);
  }

  var api = { MM_PER_IN: MM_PER_IN, CORNERS: CORNERS, TIERS: TIERS, tierLabel: tierLabel, OPTIONS: OPTIONS,
              refined: refined, hasAll: hasAll, axleBase: axleBase, fitOffset: fitOffset, plateRangeExceeded: plateRangeExceeded,
              fitted: fitted, rideHeightComp: rideHeightComp,
              parseTire: parseTire, overallDiameterMM: overallDiameterMM, sectionHeightMM: sectionHeightMM, solve: solve,
              rimText: rimText, sizeText: sizeText, tireResult: tireResult };
  root.AocHubStand = api;
  if (typeof module !== 'undefined' && module.exports) module.exports = api;

  /* ───────────────────────── page wiring ───────────────────────── */
  if (typeof document === 'undefined') return;

  var STORE_KEY = 'aoc-hub-stand';
  /* Inside Shopify's theme editor the section must show its configured state, not what
     the merchant's own browser remembered from a previous try. */
  var designMode = function () { return !!(root.Shopify && root.Shopify.designMode); };
  var fmt = function (v) { return (Math.round(v * 10) / 10).toString(); };
  var signed = function (v) { return (v > 0 ? '+' : v < 0 ? '−' : '') + Math.abs(Math.round(v)); };

  function init(rootEl) {
    if (!rootEl || rootEl.dataset.hsReady) return;
    rootEl.dataset.hsReady = '1';
    var q = function (sel) { return rootEl.querySelector(sel); };
    var qa = function (sel) { return Array.prototype.slice.call(rootEl.querySelectorAll(sel)); };
    var num = function (el) { var v = el ? parseFloat(el.value) : NaN; return isFinite(v) ? v : null; };
    var radio = function (name) { var r = rootEl.querySelector('input[name="' + name + '"]:checked'); return r ? r.value : null; };
    /* Only write to the page when the content changed: repainting the same markup on
       every keystroke makes screen readers re-announce it and wastes layout work. */
    var last = {};
    var put = function (el, key, s, asText) {
      if (!el || last[key] === s) return;
      last[key] = s;
      if (asText) el.textContent = s; else el.innerHTML = s;
    };

    /* Fill the plate-tier and tire-size lists from the one source of truth above. */
    var tierSel = q('[name="tier"]');
    TIERS.forEach(function (t) { var o = document.createElement('option'); o.value = String(t.id); o.textContent = tierLabel(t); tierSel.appendChild(o); });
    var fill = function (name, list, label, def) {
      var el = q('[name="' + name + '"]'); if (!el) return;
      list.forEach(function (v) { var o = document.createElement('option'); o.value = String(v); o.textContent = label(v); if (v === def) o.selected = true; el.appendChild(o); });
    };
    var mm = function (v) { return v + ' mm'; }, inch = function (v) { return v.toFixed(1) + ' in'; }, cm = function (v) { return v + ' cm'; };
    fill('t_width', OPTIONS.streetWidths, mm, 245); fill('t_aspect', OPTIONS.streetAspects, String, 35);
    fill('t_od_in', OPTIONS.slickODsIN, inch, 27); fill('t_width_in', OPTIONS.slickWidthsIN, inch, 11);
    fill('t_width_mm', OPTIONS.slickWidthsMM, mm, 325); fill('t_od_mm', OPTIONS.slickODsMM, mm, 705);
    fill('t_width_cm', OPTIONS.slickWidthsCM, cm, 30); fill('t_od_cm', OPTIONS.slickODsCM, cm, 68);
    fill('t_rim', OPTIONS.rims, function (v) { return rimText(v) + ' in'; }, 20);

    /* The results panel is static markup; the script only fills its values. */
    var status = q('[data-hs-status]'), panel = q('.hs__results'), empty = q('[data-hs-empty]'), grid = q('[data-hs-out]');
    var fit = q('[data-hs-fit]'), noteBase = q('[data-hs-note-base]'), noteShift = q('[data-hs-shifted]');
    var values = {};
    CORNERS.forEach(function (c) { values[c] = q('[data-hs-corner="' + c + '"] [data-hs-value]'); });

    function state() {
      var onTires = {}, onStands = {};
      CORNERS.forEach(function (c) { onTires[c] = num(q('[name="tires_' + c + '"]')); onStands[c] = num(q('[name="stands_' + c + '"]')); });
      var id = tierSel.value === '' ? null : +tierSel.value;
      return { onTires: onTires, onStands: onStands, frontSetting: num(q('[name="front_setting"]')), rearSetting: num(q('[name="rear_setting"]')),
               tier: id === null ? null : TIERS.filter(function (t) { return t.id === id; })[0] || null };
    }
    function tire() {
      return { format: radio('t_format') || 'street', width: num(q('[name="t_width"]')), aspect: num(q('[name="t_aspect"]')),
               odIN: num(q('[name="t_od_in"]')), widthIN: num(q('[name="t_width_in"]')), widthMM: num(q('[name="t_width_mm"]')), odMM: num(q('[name="t_od_mm"]')),
               widthCM: num(q('[name="t_width_cm"]')), odCM: num(q('[name="t_od_cm"]')), rim: num(q('[name="t_rim"]')),
               psi: num(q('[name="t_psi"]')), load: num(q('[name="t_load"]')), cal: num(q('[name="t_cal"]')) };
    }

    /* The visitor's Hub Mount Plate range is remembered when they pick it (it is their
       hardware, not the car's numbers). Never in the theme editor. */
    var remember = rootEl.dataset.remember === 'true' && !designMode();
    try { if (remember) { var saved = localStorage.getItem(STORE_KEY); if (saved !== null && saved !== '') tierSel.value = saved; } } catch (e) {}

    function renderTire() {
      var t = tire();
      qa('[data-hs-format]').forEach(function (el) { el.hidden = el.getAttribute('data-hs-format') !== t.format; });
      var out = q('[data-hs-tire-out]'); if (!out) return;
      var size = sizeText(t), r = tireResult(t);
      put(q('[data-hs-size]'), 'size', size, true);
      if (!r) {
        var parsed = parseTire(size);
        put(out, 'tire', '<p class="hs-problem">' + (parsed && !(sectionHeightMM(parsed) > 0)
          ? 'That overall diameter is smaller than the rim it mounts on, so there is no sidewall. Check the size.'
          : 'Enter a pressure, corner load and calibration above zero.') + '</p>');
        return;
      }
      var dia = Math.round(r.staticLoadedDiameterMM);
      put(out, 'tire',
        '<p class="hs-tire__big"><strong>' + dia + ' mm</strong> loaded diameter <span>' + (r.staticLoadedDiameterMM / MM_PER_IN).toFixed(1) + ' in</span></p>' +
        '<p class="hs-tire__meta">Nominal ' + Math.round(r.overallDiameterMM) + ' mm, deflection ' + Math.round(r.deflectionMM) + ' mm</p>' +
        (r.deflectionExceedsModelRange ? '<p class="hs-problem">Deflection is over 25% of the sidewall, where the model runs optimistic.</p>' : '') +
        (r.isSlick && t.cal === 1 ? '<p class="hs-warn">Slick size: the stiffness model was fit to street radials. Calibrate against one measured tire before trusting it.</p>' : '') +
        '<div class="hs-tire__use"><button type="button" class="hs-mini" data-hs-use="front" data-value="' + dia + '">Use ' + dia + ' mm for front</button>' +
        '<button type="button" class="hs-mini" data-hs-use="rear" data-value="' + dia + '">Use ' + dia + ' mm for rear</button></div>');
    }

    function render() {
      var st = state();
      put(q('[data-hs-echo="front"]'), 'echoF', st.frontSetting === null ? 'Not set' : fmt(st.frontSetting) + ' mm', true);
      put(q('[data-hs-echo="rear"]'), 'echoR', st.rearSetting === null ? 'Not set' : fmt(st.rearSetting) + ' mm', true);

      var any = CORNERS.some(function (c) { return refined(st, c) !== null; }), spoken = [];
      if (empty) empty.hidden = any;
      if (grid) grid.hidden = !any;
      if (noteBase) noteBase.hidden = !any;
      CORNERS.forEach(function (c) {
        var v = fitted(st, c), el = values[c];
        if (!el) return;
        put(el, 'v' + c, v === null ? '–' : fmt(v) + ' <small>mm</small>');
        el.classList.toggle('is-empty', v === null);
        if (v !== null) spoken.push(NAMES[c] + ' ' + fmt(v) + ' mm');
      });

      var k = fitOffset(st), comp = rideHeightComp(st), fitHtml = '', shiftText = '', spokenFit = '';
      if (plateRangeExceeded(st)) {
        var spread = Math.round(Math.abs(axleBase(st, 'LF') - axleBase(st, 'LR'))), span = st.tier.max - st.tier.min;
        fitHtml = '<p class="hs-problem">Front and rear differ by ' + spread + ' mm, about the full ' + span + ' mm span of one set of Hub Mount Plates, so no single shift fits all four. Recheck the Step 1 and Step 4 measurements; if the difference is real, contact CSM about your plate options.</p>';
        spokenFit = ' The front and rear settings do not fit the selected plates.';
      } else if (k) {
        fitHtml = '<div class="hs-fit"><div class="hs-fit__row"><span>Shift applied</span><strong>' + signed(k) + ' mm</strong></div>' +
                  '<div class="hs-fit__row is-comp"><span>Ride height comp<small>Add this to every ride height measurement for the true value.</small></span><strong>' + signed(-comp) + ' mm</strong></div></div>';
        shiftText = ' Shifted ' + signed(k) + ' mm to fit your ' + tierLabel(st.tier) + ' plates, keeping the front-to-rear difference. Apply the ride height comp above.';
        spokenFit = ' Shifted ' + signed(k) + ' mm to fit the plates; add ' + signed(-comp) + ' mm to every ride height measurement.';
      } else if (st.tier && !hasAll(st)) {
        fitHtml = '<p class="hs-note">Enter all four corners to fit the settings to your plate range.</p>';
      }
      put(fit, 'fit', fitHtml);
      put(noteShift, 'shift', shiftText, true);
      put(status, 'status', any ? spoken.join(', ') + '.' + spokenFit : 'Fill in Steps 1, 2 and 4 for a corner to see its setting.', true);
      stickyTop();
    }

    rootEl.addEventListener('click', function (e) {
      var use = e.target.closest('[data-hs-use]');
      if (use) {
        var field = q('[name="' + use.getAttribute('data-hs-use') + '_setting"]');
        field.value = use.getAttribute('data-value'); render(); field.focus(); return;
      }
      if (e.target.closest('[data-hs-clear]')) {
        qa('input[type="number"][data-hs-car]').forEach(function (el) { el.value = ''; });
        render();
      }
    });
    rootEl.addEventListener('input', function (e) { if (e.target.closest('[data-hs-tire]')) renderTire(); else render(); });
    rootEl.addEventListener('change', function (e) {
      if (e.target === tierSel) { try { if (remember) localStorage.setItem(STORE_KEY, tierSel.value); } catch (err) {} }
      if (e.target.closest('[data-hs-tire]')) renderTire(); else render();
    });

    /* Keep the results in view under a sticky theme header, if the theme has one, and
       only pin the panel when the whole of it fits in the viewport. */
    function stickyTop() {
      var hdr = document.querySelector('.header-section, header'), top = 16;
      if (hdr) { var pos = getComputedStyle(hdr).position; if (pos === 'sticky' || pos === 'fixed') top = hdr.getBoundingClientRect().height + 16; }
      rootEl.style.setProperty('--hs-sticky-top', top + 'px');
      if (panel) panel.classList.toggle('is-pinned', panel.offsetHeight + top + 16 <= window.innerHeight);
    }
    window.addEventListener('resize', stickyTop);
    renderTire(); render();
  }

  function initAll(scope) { (scope || document).querySelectorAll('[data-hs]').forEach(init); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { initAll(); });
  else initAll();
  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
})(typeof window !== 'undefined' ? window : globalThis);
